import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * TODO (MIN(_) or LEAST(_, _, _))
 *
 * TODO: supports a variety of data types
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface Min<out T = any> {
  /** {@link Min} */
  readonly [Key.Min]: Expression<readonly any[]> | readonly Expression<any>[]
}
export namespace Min {
  export const isMin: TypeChecker<Min> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Min>({
      [Key.Min]: CommonArgs.isExpressionOrListOfExpressions as TypeChecker<
        Min[Key.Min]
      >,
    }),
  ).usingAlias('Fn.Min')
  export const assertIsMin: TypeAsserter<Min> = TypeAsserter.Factory.from(isMin)

  /**
   * @see {@link Node}
   */
  export class Node<T = any> extends fnNode.Node<
    T,
    Key.Min,
    readonly expressionNode.Node[]
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type | undefined {
      return common.value.types.Intrinsic.Generalized.from(
        ...this.arg.map(node => node.type).filter(TypeChecker.isNotNullish.raw),
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Min, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Min>['expressionsInArgs'] = fn =>
    pack(fn[Key.Min])
  export const HELPER: FnHelper<Min> = {
    createNode: standardNodeCreator.usingExpressionList({
      // What should the min of no items be? I guess undefined
      emptyValue: undefined,
      finalizeArgs: (args, stash) => [
        ...args,
        // If we have a defined min, we include that
        ...(stash.min === undefined
          ? []
          : [new expressionNode.ForValue(stash.min)]),
      ],
      getArgExpressions: expressionsInArgs,
      handleValue: (node, stash) => {
        // NOTE: we cannot use Math.min since we want to support more than just
        // numbers
        for (const val of pack(node.value)) {
          // If we encounter a value that is less than the current min, we
          // update the min
          if (stash.min === undefined || (val as any) < stash.min) {
            stash.min = val
          }
        }
      },
      nodeClass: Node,
      setupStash: (): { min?: any } => ({}),
    }),
    expressionsInArgs,
    typeChecker: isMin,
  }
}
